import { WatchmanFetch } from '../../../shared/utils/watchmanFetch';

/**
 * Merchant storefront branding (Watchman merchant-store-config). Reads the admin
 * view and writes the logo/name/description via the admin-by-id business-profile
 * route (PATCH /merchants/:id/business-profile) — the superadmin counterpart to the
 * merchant's own `me/business-profile`. Routed through osprey-api's /iam proxy.
 */

/**
 * Runtime brand imagery persisted on the merchant store-config (PE brand-assets). Maps 1:1 onto the
 * storefront's static MerchantConfig imagery slots; the storefront reads it at runtime and overlays
 * it. Every field is optional — anything unset keeps the storefront's static default.
 */
export interface BrandAssets {
  /** → storefront brand.logo.image (mirrors storeLogoUrl) */
  logoImage?: string;
  /** → storefront brand.favicon */
  favicon?: string;
  /** → storefront brand.ogImage */
  ogImage?: string;
  /** → storefront content.hero.media (ordered; collage uses 3) */
  hero?: string[];
  /** → storefront content.community (ordered) */
  community?: string[];
  /** → storefront content.collections[].image, matched by label */
  collections?: { label: string; image: string }[];
  /** → storefront taxonomy.occasions[].image, matched by label */
  occasions?: { label: string; image: string }[];
  /** → storefront content.about.image */
  about?: string;
  /** → storefront content.story.image */
  story?: string;
}

export interface MerchantStoreConfig {
  merchantId: string;
  storeName?: string | null;
  storeLogoUrl?: string | null;
  description?: string | null;
  category?: string | null;
  storeSlug?: string | null;
  customDomain?: string | null;
  brandAssets?: BrandAssets | null;
}

export interface UpdateBusinessProfilePayload {
  storeName?: string;
  storeLogoUrl?: string;
  description?: string;
  category?: string;
  brandAssets?: BrandAssets;
}

export const storeConfigApi = {
  /** Admin read of a merchant's store config (creates an empty row on first access). */
  get: (merchantId: string): Promise<MerchantStoreConfig> =>
    WatchmanFetch.get(`/merchants/${merchantId}/store-config`),

  /** Admin write of storefront branding by merchant id. */
  updateBusinessProfile: (
    merchantId: string,
    data: UpdateBusinessProfilePayload,
  ): Promise<MerchantStoreConfig> =>
    WatchmanFetch.patch(`/merchants/${merchantId}/business-profile`, data),

  /**
   * Admin sets the merchant's real production domain (e.g. brand.com) by id. Used for the
   * storefront and to build the per-merchant payment return URL. Pass null to clear it.
   */
  setCustomDomain: (
    merchantId: string,
    customDomain: string | null,
  ): Promise<{ customDomain: string | null }> =>
    WatchmanFetch.patch(`/merchants/${merchantId}/custom-domain`, { customDomain }),
};
